/**
 * InteractivePicker.js — bottom sheet that picks an interactive message
 * type, fills its form, previews the result, and sends.
 *
 * Two-stage flow in a single sheet (mirrors TemplatePicker UX so it feels
 * native to agents who learned that pattern):
 *
 *   1. TILES — 2-up grid of types from INTERACTIVE_TYPES registry
 *   2. FORM  — type-specific fields + live preview, then Send
 *
 * The form is intentionally inline rather than per-type files. Three types
 * isn't enough to justify the file split, and one place to read keeps the
 * "what does this type look like" cognitive load low. When we add the 4th
 * type later, splitting becomes worth it.
 *
 * Mobile-first:
 *   - Sheet caps at 88vh so the textarea has room without overflowing the
 *     viewport on iPhone
 *   - Preview pinned at top so it stays in view while typing
 *   - Optional fields hidden under "+ Add header / footer" so the form is
 *     short on first open
 */

import { html } from 'htm/preact';
import { useMemo, useState } from 'preact/hooks';

import { $sheets, showToast, $openChatPhone, $openChatChannel, $openChatId } from '../stores/ui.js';
import { $chats } from '../stores/chats.js';
import { INTERACTIVE_TYPES, sendInteractive } from '../services/interactives.js';
import * as api from '../services/api.js';
import { addScheduled } from '../stores/scheduled.js';
import { SchedulePopover } from './SchedulePopover.js';

const TYPES = Object.values(INTERACTIVE_TYPES);

function _trim(s) { return String(s || '').trim(); }

export function InteractivePicker({ onClose }) {
  const [selectedId, setSelectedId] = useState(null);
  const [state, setState]           = useState(null);
  const [showOptional, setShowOptional] = useState(false);
  const [sending, setSending]       = useState(false);
  const [error, setError]           = useState(null);
  // T9 — Schedule popover toggle. Opened from the Schedule pill next
  // to the Send button; reuses the same component the composer uses
  // so the two scheduling flows stay visually identical.
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const def = useMemo(
    () => (selectedId ? INTERACTIVE_TYPES[selectedId] : null),
    [selectedId]
  );

  const pick = (id) => {
    const d = INTERACTIVE_TYPES[id];
    setSelectedId(id);
    setState({ ...d.defaultState });
    setShowOptional(false);
    setError(null);
  };

  const back = () => {
    setSelectedId(null);
    setState(null);
    setError(null);
  };

  const update = (patch) => setState(s => ({ ...s, ...patch }));

  const handleSend = async () => {
    if (sending) return;
    setError(null);
    const phone = $openChatPhone.get();
    if (!phone) {
      setError('No active chat — open one first.');
      return;
    }
    const validationErr = def.validate(state);
    if (validationErr) {
      setError(validationErr);
      return;
    }
    setSending(true);
    try {
      await sendInteractive(selectedId, phone, state);
      showToast('Interactive message sent', 1800);
      onClose?.();
      $sheets.setKey('interactive', false);
    } catch (e) {
      console.error('[v2 interactive] send failed:', e.message);
      setError(e.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  // T9 — Schedule the currently-composed interactive message (delay or
  // keep-alive). Reuses the same POST /scheduled route as the text
  // composer — only difference is `message_type:'interactive'` and the
  // payload comes from def.buildPayload instead of the composer textarea.
  //
  // Keep-alive + interactive is the highest-value combo per product
  // judgment: button-based messages get reply rates orders of magnitude
  // higher than plain text, which is exactly what a keep-alive needs
  // (a reply resets the 24h session window).
  const handleSchedule = async (schedType, delayMs, triggerMs) => {
    if (!def || !state) return;
    const phone = $openChatPhone.get();
    if (!phone) {
      setError('No active chat — open one first.');
      return;
    }
    const validationErr = def.validate(state);
    if (validationErr) {
      setError(validationErr);
      setScheduleOpen(false);
      return;
    }
    setSending(true);
    try {
      const payload = def.buildPayload(state, phone);
      const channelPhoneId = $openChatChannel.get() || null;
      const result = await api.createScheduledMessage({
        chat_id:       phone,
        channel_id:    channelPhoneId,
        schedule_type: schedType,
        message_type:  'interactive',
        payload,
        delay_ms:      delayMs,
        trigger_offset_ms: triggerMs,
      });
      const created = result?.data || result;
      if (created?.id) {
        const schedKey = channelPhoneId ? `${phone}:${channelPhoneId}` : phone;
        addScheduled(schedKey, created);
      }
      const eta = delayMs
        ? new Date(Date.now() + delayMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : 'before session expires';
      showToast(`Scheduled for ${eta}`, 2500);
      setScheduleOpen(false);
      onClose?.();
      $sheets.setKey('interactive', false);
    } catch (e) {
      console.error('[v2 interactive] schedule failed:', e.message);
      setError(e.message || 'Schedule failed');
    } finally {
      setSending(false);
    }
  };

  // T9 — Surface the 24h session window state to the popover so it can
  // disable infeasible keep-alive chips (T5 guard). Looked up from the
  // open chat row, not fetched — InteractivePicker mounts inside an
  // active thread so the store always has it.
  const _currentChatLastInboundTs = () => {
    const openId = $openChatId.get();
    if (!openId) return null;
    const chat = $chats.get()[openId];
    return chat?.lastInboundTs || null;
  };

  // ── Reply-button row helpers ────────────────────────────────────────────
  const setBtn = (i, value) => {
    const next = [...state.buttons];
    next[i] = { title: value };
    update({ buttons: next });
  };
  const addBtn = () => {
    if ((state.buttons || []).length >= 3) return;
    update({ buttons: [...state.buttons, { title: '' }] });
  };
  const removeBtn = (i) => {
    if ((state.buttons || []).length <= 1) return;
    update({ buttons: state.buttons.filter((_, idx) => idx !== i) });
  };

  // ── Preview ─────────────────────────────────────────────────────────────
  // Mocks the WA bubble: header (text) → body → footer → action area
  // (button stack for reply_buttons + cta_url, "Send location" pill for
  // location_request). Plain HTML, no CSS module dependency.
  const renderPreview = () => {
    if (!def || !state) return null;
    const previewBox = (children) => html`
      <div style="background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.08);border-radius:12px;overflow:hidden">
        ${children}
      </div>
    `;

    if (def.id === 'reply_buttons') {
      return previewBox(html`
        ${state.header && html`<div style="padding:12px 14px 6px;font-weight:600;font-size:0.92rem">${state.header}</div>`}
        <div style="padding:${state.header ? '0 14px 12px' : '12px 14px'};font-size:0.85rem;white-space:pre-wrap;line-height:1.45">${state.body || html`<span style="opacity:0.4">Body preview…</span>`}</div>
        ${state.footer && html`<div style="padding:0 14px 10px;font-size:0.72rem;color:var(--text-muted, #7d8799)">${state.footer}</div>`}
        <div style="display:flex;flex-direction:column;gap:1px;border-top:1px solid rgba(255,255,255,0.08)">
          ${(state.buttons || []).filter(b => _trim(b.title)).map(b => html`
            <div style="padding:10px 14px;text-align:center;font-size:0.82rem;color:#60a5fa;font-weight:500">${b.title}</div>
          `)}
        </div>
      `);
    }

    if (def.id === 'cta_url') {
      return previewBox(html`
        ${state.header && html`<div style="padding:12px 14px 6px;font-weight:600;font-size:0.92rem">${state.header}</div>`}
        <div style="padding:${state.header ? '0 14px 12px' : '12px 14px'};font-size:0.85rem;white-space:pre-wrap;line-height:1.45">${state.body || html`<span style="opacity:0.4">Body preview…</span>`}</div>
        ${state.footer && html`<div style="padding:0 14px 10px;font-size:0.72rem;color:var(--text-muted, #7d8799)">${state.footer}</div>`}
        <div style="border-top:1px solid rgba(255,255,255,0.08);padding:10px 14px;text-align:center;font-size:0.82rem;color:#60a5fa;font-weight:500">
          🔗 ${state.label || 'Visit website'}
        </div>
      `);
    }

    if (def.id === 'location_request') {
      return previewBox(html`
        <div style="padding:12px 14px;font-size:0.85rem;white-space:pre-wrap;line-height:1.45">${state.body || html`<span style="opacity:0.4">Body preview…</span>`}</div>
        <div style="border-top:1px solid rgba(255,255,255,0.08);padding:10px 14px;text-align:center;font-size:0.82rem;color:#25D366;font-weight:500;display:flex;align-items:center;justify-content:center;gap:6px">
          📍 Send location
        </div>
      `);
    }
    return null;
  };

  // ── Form per type ───────────────────────────────────────────────────────
  const renderForm = () => {
    if (!def || !state) return null;

    const optionalToggle = html`
      <button
        type="button"
        class="inbox-btn-link"
        style="font-size:0.78rem;padding:0;margin:6px 0 10px"
        onClick=${() => setShowOptional(v => !v)}
      >
        ${showOptional ? '− Hide header / footer' : '+ Add header / footer'}
      </button>
    `;

    const optionalFields = showOptional && html`
      <div style="margin-top:6px">
        <label style="display:block;font-size:0.72rem;color:var(--text-muted, #7d8799);margin-bottom:4px">Header (optional, max 60)</label>
        <input
          type="text"
          maxLength="60"
          value=${state.header || ''}
          onInput=${(e) => update({ header: e.target.value })}
          style="width:100%;background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.1);border-radius:8px;padding:9px 11px;color:inherit;font-size:0.85rem;margin-bottom:10px"
        />
        <label style="display:block;font-size:0.72rem;color:var(--text-muted, #7d8799);margin-bottom:4px">Footer (optional, max 60)</label>
        <input
          type="text"
          maxLength="60"
          value=${state.footer || ''}
          onInput=${(e) => update({ footer: e.target.value })}
          style="width:100%;background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.1);border-radius:8px;padding:9px 11px;color:inherit;font-size:0.85rem"
        />
      </div>
    `;

    if (def.id === 'reply_buttons') {
      return html`
        <div>
          <label style="display:block;font-size:0.72rem;color:var(--text-muted, #7d8799);margin-bottom:4px">Body (required, max 1024)</label>
          <textarea
            rows="3"
            maxLength="1024"
            value=${state.body}
            onInput=${(e) => update({ body: e.target.value })}
            placeholder="Type your question here…"
            style="width:100%;background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.1);border-radius:8px;padding:9px 11px;color:inherit;font-size:0.85rem;font-family:inherit;resize:vertical"
          ></textarea>

          ${(def.defaultState.header !== undefined || def.defaultState.footer !== undefined) && optionalToggle}
          ${optionalFields}

          <div style="display:flex;align-items:center;justify-content:space-between;margin-top:6px;margin-bottom:6px">
            <label style="font-size:0.72rem;color:var(--text-muted, #7d8799)">Buttons (1–3, max 20 chars each)</label>
            ${(state.buttons || []).length < 3 && html`
              <button type="button" class="inbox-btn-link" style="font-size:0.78rem;padding:0" onClick=${addBtn}>+ Add button</button>
            `}
          </div>
          ${(state.buttons || []).map((b, i) => html`
            <div key=${i} style="display:flex;align-items:center;gap:6px;margin-bottom:8px">
              <input
                type="text"
                maxLength="20"
                value=${b.title}
                onInput=${(e) => setBtn(i, e.target.value)}
                placeholder="Button label"
                style="flex:1;background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.1);border-radius:8px;padding:9px 11px;color:inherit;font-size:0.85rem"
              />
              ${(state.buttons || []).length > 1 && html`
                <button
                  type="button"
                  onClick=${() => removeBtn(i)}
                  style="background:transparent;border:none;color:var(--text-muted, #7d8799);font-size:1.1rem;cursor:pointer;padding:4px 8px"
                  aria-label="Remove button"
                >×</button>
              `}
            </div>
          `)}
        </div>
      `;
    }

    if (def.id === 'cta_url') {
      return html`
        <div>
          <label style="display:block;font-size:0.72rem;color:var(--text-muted, #7d8799);margin-bottom:4px">Body (required, max 1024)</label>
          <textarea
            rows="3"
            maxLength="1024"
            value=${state.body}
            onInput=${(e) => update({ body: e.target.value })}
            placeholder="Tell the customer why they should tap…"
            style="width:100%;background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.1);border-radius:8px;padding:9px 11px;color:inherit;font-size:0.85rem;font-family:inherit;resize:vertical"
          ></textarea>

          <label style="display:block;font-size:0.72rem;color:var(--text-muted, #7d8799);margin:10px 0 4px">Button label (required, max 20)</label>
          <input
            type="text"
            maxLength="20"
            value=${state.label}
            onInput=${(e) => update({ label: e.target.value })}
            placeholder="Visit website"
            style="width:100%;background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.1);border-radius:8px;padding:9px 11px;color:inherit;font-size:0.85rem"
          />

          <label style="display:block;font-size:0.72rem;color:var(--text-muted, #7d8799);margin:10px 0 4px">URL (required)</label>
          <input
            type="url"
            value=${state.url}
            onInput=${(e) => update({ url: e.target.value })}
            placeholder="https://example.com"
            style="width:100%;background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.1);border-radius:8px;padding:9px 11px;color:inherit;font-size:0.85rem"
          />

          ${optionalToggle}
          ${optionalFields}
        </div>
      `;
    }

    if (def.id === 'location_request') {
      return html`
        <div>
          <label style="display:block;font-size:0.72rem;color:var(--text-muted, #7d8799);margin-bottom:4px">Body (required, max 1024)</label>
          <textarea
            rows="3"
            maxLength="1024"
            value=${state.body}
            onInput=${(e) => update({ body: e.target.value })}
            placeholder="Why do you need their location?"
            style="width:100%;background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.1);border-radius:8px;padding:9px 11px;color:inherit;font-size:0.85rem;font-family:inherit;resize:vertical"
          ></textarea>
          <div style="margin-top:10px;padding:10px 12px;background:rgba(96,165,250,0.10);border:1px solid rgba(96,165,250,0.3);border-radius:10px;font-size:0.74rem;color:#bfdbfe">
            WhatsApp will show a "Send location" button to the customer. They tap it to share their current GPS location with you.
          </div>
        </div>
      `;
    }
    return null;
  };

  return html`
    <div class="inbox-sheet-overlay" style="display:block" onClick=${onClose}></div>
    <div
      class="inbox-bottom-sheet"
      style="display:flex;flex-direction:column;max-height:88vh;min-height:50vh;background:var(--v2-bg-elevated, #131722)"
      role="dialog"
      aria-modal="true"
      aria-label="Send interactive message"
      onClick=${(e) => e.stopPropagation()}
    >
      <div class="inbox-sheet-handle"></div>

      <div class="inbox-sheet-header" style="flex-shrink:0">
        ${def && html`
          <button
            class="inbox-header-icon-btn"
            onClick=${back}
            title="Back"
            style="margin-right:4px"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="m15 18-6-6 6-6"/>
            </svg>
          </button>
        `}
        <h3 class="inbox-sheet-title" style="margin:0;flex:1;min-width:0">
          ${def ? def.label : 'Send interactive'}
        </h3>
        <button class="inbox-sheet-close" onClick=${onClose}>×</button>
      </div>

      ${!def && html`
        <!-- Type picker grid -->
        <div style="padding:14px;flex:1;overflow-y:auto">
          <p style="margin:0 0 14px;font-size:0.78rem;color:var(--text-muted, #7d8799)">
            Pick the kind of interactive message to send. The customer will see it in their WhatsApp chat with the platform's native UI for buttons, links, or location share.
          </p>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">
            ${TYPES.map(t => html`
              <button
                key=${t.id}
                class="inbox-interactive-tile"
                onClick=${() => pick(t.id)}
                style="display:flex;flex-direction:column;align-items:flex-start;gap:6px;padding:14px;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.08);border-radius:12px;color:inherit;cursor:pointer;text-align:left;min-height:96px"
              >
                <span style="font-size:1.6rem;line-height:1">${t.icon}</span>
                <span style="font-weight:600;font-size:0.88rem">${t.label}</span>
                <span style="font-size:0.7rem;color:var(--text-muted, #7d8799)">${t.hint}</span>
              </button>
            `)}
          </div>
        </div>
      `}

      ${def && html`
        <!-- Form + preview -->
        <div style="flex:1;overflow-y:auto;padding:0 14px 16px">
          <!-- Sticky preview at top -->
          <div style="margin-top:10px">${renderPreview()}</div>

          <!-- Form -->
          <div style="margin-top:14px">${renderForm()}</div>

          ${error && html`
            <div style="margin-top:10px;padding:8px 12px;background:rgba(239,68,68,0.12);border:1px solid rgba(239,68,68,0.4);border-radius:8px;color:#fca5a5;font-size:0.78rem">
              ${error}
            </div>
          `}
        </div>

        <!-- Send action + Schedule pill -->
        <!--
          T9 — Two-button footer. Primary Send stays full-width-looking
          (via flex:1) to preserve conversion-focused CTA density; the
          Schedule pill is a compact secondary action flanking it. A
          split keeps both paths equally discoverable without hiding
          Schedule behind a long-press gesture, which has poor
          affordance on mobile (ui-ux-pro-max 'Disabled States' + MD3
          guidance favors visible chips over hidden gestures).
        -->
        <div style="flex-shrink:0;padding:12px 14px calc(14px + var(--safe-bottom, 0px));border-top:1px solid rgba(255,255,255,0.08);background:var(--v2-bg-elevated, #131722);position:relative">
          <div style="display:flex;gap:8px;align-items:stretch">
            <button
              class="inbox-interactive-send-btn"
              onClick=${handleSend}
              disabled=${sending}
              style="flex:1;background:#25D366;color:#0a0c12;font-weight:700;font-size:0.92rem;padding:13px;border:none;border-radius:12px;cursor:${sending ? 'wait' : 'pointer'};opacity:${sending ? 0.7 : 1}"
            >
              ${sending ? 'Sending…' : 'Send to customer'}
            </button>
            <button
              onClick=${() => setScheduleOpen(v => !v)}
              disabled=${sending}
              aria-label="Schedule this interactive message"
              title="Schedule — fire later or right before the session expires"
              style="display:inline-flex;align-items:center;gap:6px;background:rgba(96,165,250,0.08);color:#93c5fd;font-weight:700;font-size:0.82rem;padding:13px 14px;border:1px solid rgba(96,165,250,0.25);border-radius:12px;cursor:${sending ? 'wait' : 'pointer'};opacity:${sending ? 0.6 : 1};font-family:inherit"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              Schedule
            </button>
          </div>
          ${scheduleOpen && html`
            <${SchedulePopover}
              phone=${$openChatPhone.get()}
              channelPhoneId=${$openChatChannel.get()}
              lastInboundTs=${_currentChatLastInboundTs()}
              onClose=${() => setScheduleOpen(false)}
              onScheduled=${(schedType, delayMs, triggerMs) => {
                handleSchedule(schedType, delayMs, triggerMs);
              }}
            />
          `}
        </div>
      `}
    </div>
  `;
}
